import React, { useState, useEffect } from 'react';
import './QuizResults.css';

const QuizResults = ({ quiz, questions, userAnswers, onRestart, onRestartWithSettings, onBack }) => {
  const [expandedExplanations, setExpandedExplanations] = useState(new Set());
  const score = userAnswers.reduce((total, answer) => total + (answer.isCorrect ? 1 : 0), 0);
  const totalQuestions = questions.length;
  const percentage = Math.round((score / totalQuestions) * 100);

  // 結果画面表示時にページ先頭へ自動スクロール
  useEffect(() => {
    // レイアウト確定後でも確実に先頭へ移動
    window.scrollTo(0, 0);
    setTimeout(() => window.scrollTo(0, 0), 0);
  }, []);

  const getScoreMessage = () => {
    if (percentage >= 90) return { message: '素晴らしい！', emoji: '🎉', color: '#28a745' };
    if (percentage >= 70) return { message: 'よくできました！', emoji: '👏', color: '#007bff' };
    if (percentage >= 50) return { message: 'もう少し頑張りましょう', emoji: '📝', color: '#ffc107' };
    return { message: '復習してみましょう', emoji: '📚', color: '#dc3545' };
  };

  const scoreInfo = getScoreMessage();

  const toggleExplanation = (questionId) => {
    const newExpanded = new Set(expandedExplanations);
    if (newExpanded.has(questionId)) {
      newExpanded.delete(questionId);
    } else {
      newExpanded.add(questionId);
    }
    setExpandedExplanations(newExpanded);
  };

  return (
    <div className="quiz-results">
      <div className="results-header">
        <h2>クイズ結果</h2>
        <div className="score-display" style={{ borderColor: scoreInfo.color }}>
          <div className="score-emoji">{scoreInfo.emoji}</div>
          <div className="score-text">
            <span className="score-fraction">{score} / {totalQuestions}</span>
            <span className="score-percentage">({percentage}%)</span>
          </div>
          <div className="score-message" style={{ color: scoreInfo.color }}>
            {scoreInfo.message}
          </div>
        </div>
      </div>

      <div className="quiz-info">
        <h3>{quiz.title}</h3>
        <p>{quiz.description}</p>
      </div>

      <div className="results-details">
        <h4>詳細結果</h4>
        {questions.map((question, index) => {
          const userAnswer = userAnswers[index];
          const correctChoices = (question.choices || []).filter(choice => choice.isCorrect);
          
          if (!userAnswer) {
            return (
              <div key={question.id || `question-${index}`} className="result-item">
                <div className="result-header">
                  <span className="question-number">問題 {index + 1}</span>
                  <span className="result-status incorrect">未回答</span>
                </div>
                <div className="result-question">
                  <p className="question-text">{question.questionText}</p>
                </div>
              </div>
            );
          }
          
          return (
            <div key={question.id || `question-${index}`} className="result-item">
              <div className="result-header">
                <span className="question-number">問題 {index + 1}</span>
                <span className={`result-status ${userAnswer.isCorrect ? 'correct' : 'incorrect'}`}>
                  {userAnswer.isCorrect ? '正解' : '不正解'}
                </span>
              </div>
              
              <div className="result-question">
                <p className="question-text">{question.questionText}</p>
                
                {question.tags && question.tags.length > 0 && (
                  <div className="question-tags">
                    {question.tags.map((tag, tagIndex) => (
                      <span key={tagIndex} className="tag">{tag}</span>
                    ))}
                  </div>
                )}
              </div>

              <div className="result-answers">
                <div className="answer-section">
                  <h5>あなたの回答:</h5>
                  <div className="user-choices">
                    {(userAnswer.selectedChoiceIds || []).map(choiceId => {
                      const choice = (question.choices || []).find(c => c.id === choiceId);
                      if (!choice) {
                        return (
                          <span key={choiceId} className="choice-tag incorrect">
                            選択肢が見つかりません
                          </span>
                        );
                      }
                      return (
                        <span key={choiceId} className={`choice-tag ${choice.isCorrect ? 'correct' : 'incorrect'}`}>
                          {choice.text}
                        </span>
                      );
                    })}
                  </div>
                </div>

                <div className="answer-section">
                  <h5>正解:</h5>
                  <div className="correct-choices">
                    {correctChoices.map(choice => (
                      <span key={choice.id} className="choice-tag correct">
                        {choice.text}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {question.explanation && (
                <div className="explanation">
                  <div 
                    className="explanation-header"
                    onClick={() => toggleExplanation(question.id || `question-${index}`)}
                  >
                    <h5>解説:</h5>
                    <span className="explanation-toggle">
                      {expandedExplanations.has(question.id || `question-${index}`) ? '▼' : '▶'}
                    </span>
                  </div>
                  {expandedExplanations.has(question.id || `question-${index}`) && (
                    <div className="explanation-content">
                      <p>{question.explanation}</p>
                      {question.explanationImageUrl && (
                        <img 
                          src={question.explanationImageUrl} 
                          alt="解説図" 
                          className="explanation-image"
                        />
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="results-actions">
        <button onClick={onRestartWithSettings || onRestart} className="action-btn restart-btn">
          もう一度挑戦
        </button>
        <button onClick={onBack} className="action-btn back-btn">
          クイズ一覧に戻る
        </button>
      </div>
    </div>
  );
};

export default QuizResults;
